import { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import {
  X, Globe, Activity, ExternalLink, AlertTriangle, ShieldAlert, Server, AlertOctagon, Crosshair,
} from "lucide-react";
import { redirectToLogin } from "../utils/auth";

const FROSTED_CARD = "bg-[#0a1224]/70 border border-sky-900/30 rounded-md";

// Recorded Future-style ranked "Top N" bar list. Width is relative to the
// leading value; share is % of the visible total. Pure CSS — no chart lib.
function RankedBars({ title, icon: Icon, items, unit = "victims" }) {
  const max = items.length ? Math.max(...items.map((i) => i.value)) : 0;
  const total = items.reduce((s, i) => s + (Number(i.value) || 0), 0);
  return (
    <div className={`${FROSTED_CARD} p-5`}>
      <h3 className="text-[11px] uppercase tracking-wider font-semibold text-neutral-300 flex items-center gap-2 mb-4">
        {Icon && <Icon className="h-3.5 w-3.5 text-neutral-500" />} {title}
      </h3>
      {items.length === 0 ? (
        <div className="text-[12px] text-neutral-500 py-3 text-center">No {unit} data available</div>
      ) : (
        <ul className="space-y-3">
          {items.map((it) => {
            const pct = total > 0 ? Math.round((it.value / total) * 100) : 0;
            const w = max > 0 ? Math.max(2, (it.value / max) * 100) : 0;
            return (
              <li key={it.name}>
                <div className="flex items-center justify-between gap-3 text-[12px] mb-1">
                  <span className="text-neutral-200 truncate">{it.name}</span>
                  <span className="shrink-0 tabular-nums text-neutral-400">
                    {it.value.toLocaleString()} <span className="text-neutral-600">· {pct}%</span>
                  </span>
                </div>
                <div className="h-1.5 w-full rounded-full bg-white/[0.04] overflow-hidden">
                  <div className="h-full rounded-full bg-sky-600/80" style={{ width: `${w}%` }} />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Compact attribute cell for the intelligence-summary grid.
function SummaryStat({ label, value }) {
  return (
    <div className="px-4 py-3">
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 font-medium">{label}</div>
      <div className="mt-1 text-[15px] font-semibold text-neutral-100 tabular-nums truncate">{value}</div>
    </div>
  );
}

// --- HELPER FUNCTIONS ---

function extractThreatFeedAccess(json) {
  try {
    if (!json || typeof json !== "object") return false;
    if (Object.prototype.hasOwnProperty.call(json, "threat_feed")) {
      const v = json.threat_feed;
      return v === 1 || v === "1" || v === true || v === "true";
    }
    if (json.data && typeof json.data === "object" && "threat_feed" in json.data) {
      const v = json.data.threat_feed;
      return v === 1 || v === "1" || v === true || v === "true";
    }
    if (json.subscription && typeof json.subscription === "object" && "threat_feed" in json.subscription) {
      const v = json.subscription.threat_feed;
      return v === 1 || v === "1" || v === true || v === "true";
    }
  } catch (_) {}
  return false;
}

function UpgradeCallout() {
  const go = () => {
    try { window.location.href = "/upgrades"; } catch {}
  };
 return (
    <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-amber-100">
      <div className="flex items-start gap-3">
        <ShieldAlert className="h-5 w-5 mt-0.5" />
        <div>
          <div className="font-semibold">Threat Feed is locked</div>
          <div className="text-sm opacity-90">
            Your current subscription doesn’t include Threat Feed. Upgrade to
            unlock full intel details, sources and historical context.
          </div>
          <div className="mt-2">
            <button
              onClick={go}
              className="rounded-md border border-amber-500/40 bg-transparent px-3 py-1.5 text-sm hover:bg-amber-500/10"
            >
              Upgrade plan
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- MAIN COMPONENT ---

export default function DetailSidebarActor({ open, onClose, slug, jwtToken, apiUrl }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [redirectUrl, setRedirectUrl] = useState(null);
  const [hasThreatFeedAccess, setHasThreatFeedAccess] = useState(null);

  useEffect(() => {
    if (!open || !slug) return;
    setLoading(true);
    setError("");
    setData(null);
    setHasThreatFeedAccess(null);

    fetch(apiUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${jwtToken}` },
      body: JSON.stringify({ metric: "detail", slug }),
    })
      .then(async (res) => {
        if ([401, 419, 440].includes(res.status)) {
          redirectToLogin();
          throw new Error(`AUTH_REDIRECT_${res.status}`);
        }
        if (res.status === 403) {
            setHasThreatFeedAccess(false);
            return null; 
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        return json;
      })
      .then((j) => {
        if (!j) return; 

        if (j.status === "error") {
            if (j.message && j.message.toLowerCase().includes("not allowed")) {
                setHasThreatFeedAccess(false);
            } else {
                throw new Error(j.message);
            }
        } else {
            setData(j.data);
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [open, slug, jwtToken, apiUrl]);

  const chartData = (jsonObj) => {
    if (!jsonObj || typeof jsonObj !== 'object') return [];
    return Object.entries(jsonObj)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  };
  const distinctCount = (jsonObj) =>
    jsonObj && typeof jsonObj === "object" ? Object.keys(jsonObj).length : 0;

  const formatDate = (d) => d ? new Date(d).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—";

  // Sorted descending by value, top 8 — RF "Top N" victimology lists.
  const geoData = data?.profile?.stats_geo ? chartData(data.profile.stats_geo) : [];
  const vectorData = data?.profile?.stats_vector ? chartData(data.profile.stats_vector) : [];
  const industryData = data?.profile?.stats_industry ? chartData(data.profile.stats_industry) : [];

  const countriesTargeted = distinctCount(data?.profile?.stats_geo);
  const industriesTargeted = distinctCount(data?.profile?.stats_industry);

  const locked = hasThreatFeedAccess === false;

  return (
    <>
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-[200]" onClose={onClose}>
        {/* BACKDROP */}
        <Transition.Child
            as={Fragment}
            enter="ease-in-out duration-500"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in-out duration-500"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
        >
            <div className="fixed inset-0 bg-black/60 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-500 sm:duration-700"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-500 sm:duration-700"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-2xl">
                  <div className="flex h-full flex-col bg-[#06101e] border-l border-sky-500/20 shadow-[0_0_50px_rgba(0,0,0,0.5)]">
                    
                    {/* Header */}
                    <div className="px-6 py-5 border-b border-sky-900/30 bg-[#0a1224]/60">
                      <div className="text-[10.5px] uppercase tracking-[0.18em] text-sky-300/70 font-semibold mb-2">
                        Threat Actor
                      </div>
                      <div className="flex items-start justify-between gap-4">
                        <div className="min-w-0">
                          {loading ? (
                             <div className="h-6 w-48 bg-white/10 rounded animate-pulse" />
                          ) : (
                             <Dialog.Title className="text-[20px] font-semibold text-white tracking-tight flex items-center gap-2.5">
                               {locked ? "Restricted Access" : (data?.profile?.name || "Loading…")}
                               {!locked && data?.profile?.status === "Active" && (
                                  <span className="inline-flex h-2 w-2 rounded-full bg-emerald-500" title="Active" />
                               )}
                             </Dialog.Title>
                          )}

                          {!locked && !loading && data && (
                              <div className="mt-2.5 flex flex-wrap items-center gap-x-4 gap-y-1 text-[12px] text-neutral-400">
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="text-neutral-500">Status:</span>
                                    <span className={data?.profile?.status === "Active" ? "text-emerald-300 font-medium" : "text-neutral-300"}>
                                        {data?.profile?.status || "Unknown"}
                                    </span>
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="text-neutral-500">First seen:</span>
                                    <span className="text-neutral-200 tabular-nums">{formatDate(data?.profile?.first_seen)}</span>
                                </span>
                                <span className="inline-flex items-center gap-1.5">
                                    <span className="text-neutral-500">Last seen:</span>
                                    <span className="text-neutral-200 tabular-nums">{formatDate(data?.profile?.last_seen)}</span>
                                </span>
                              </div>
                          )}
                        </div>
                        <button onClick={onClose} className="shrink-0 rounded-md p-1.5 text-neutral-500 hover:text-white hover:bg-white/[0.06] transition-colors">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>

                    {/* Content */}
                    <div className="flex-1 overflow-y-auto p-6 space-y-8 scrollbar-thin scrollbar-thumb-sky-900/50 scrollbar-track-transparent">
                      
                      {loading && (
                         <div className="space-y-6">
                            <div className="grid grid-cols-2 gap-4">
                               <div className="h-24 bg-white/5 rounded-md animate-pulse" />
                               <div className="h-24 bg-white/5 rounded-md animate-pulse" />
                            </div>
                            <div className="h-64 bg-white/5 rounded-md animate-pulse" />
                         </div>
                      )}

                      {/* --- LOCKED STATE --- */}
                      {locked && !loading && (
                          <UpgradeCallout />
                      )}

                      {/* --- ERROR STATE --- */}
                      {error && !locked && !loading && (
                         <div className="p-4 rounded-md border border-red-500/20 bg-red-500/10 text-red-200 flex items-center gap-3">
                            <AlertTriangle className="h-5 w-5" /> {error}
                         </div>
                      )}
                      
                      {/* --- DATA DISPLAY --- */}
                      {data && !loading && !locked && (
                        <>
                           {/* Intelligence summary — dense attribute grid (RF leads
                               with the analyst's at-a-glance scope, not big charts). */}
                           <div className={`${FROSTED_CARD} grid grid-cols-2 sm:grid-cols-4 divide-x divide-y sm:divide-y-0 divide-sky-900/30`}>
                              <SummaryStat label="Confirmed Victims" value={Number(data.profile.total_incidents || 0).toLocaleString()} />
                              <SummaryStat label="Countries Hit" value={countriesTargeted.toLocaleString()} />
                              <SummaryStat label="Industries Hit" value={industriesTargeted.toLocaleString()} />
                              <SummaryStat label="Primary TTP" value={vectorData.length > 0 ? vectorData[0].name : "—"} />
                           </div>

                           {/* Victimology — three consistent ranked "Top N" lists. */}
                           <RankedBars title="Top Targeted Industries" icon={Server} items={industryData} unit="industry" />
                           <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <RankedBars title="Top Victim Countries" icon={Globe} items={geoData} unit="geography" />
                              <RankedBars title="Top Attack Vectors" icon={Crosshair} items={vectorData} unit="vector" />
                           </div>

                           {/* Recently Observed — dense referenced-events table. */}
                           <div className={`${FROSTED_CARD} overflow-hidden`}>
                              <div className="px-4 py-2.5 border-b border-sky-900/30 bg-[#0b1426]/60 flex items-center gap-2">
                                 <Activity className="h-3.5 w-3.5 text-neutral-500" />
                                 <span className="text-[11px] uppercase tracking-wider font-semibold text-neutral-300">Recently Observed</span>
                              </div>
                              {data.feed && data.feed.length > 0 ? (
                                 <div className="overflow-x-auto">
                                    <table className="w-full text-[12px]">
                                       <thead className="text-neutral-500 border-b border-sky-900/30">
                                          <tr>
                                             <th className="text-left font-medium uppercase tracking-wider text-[10px] px-4 py-2">Date</th>
                                             <th className="text-left font-medium uppercase tracking-wider text-[10px] px-4 py-2">Victim</th>
                                             <th className="text-left font-medium uppercase tracking-wider text-[10px] px-4 py-2 hidden sm:table-cell">Industry</th>
                                             <th className="text-left font-medium uppercase tracking-wider text-[10px] px-4 py-2 hidden sm:table-cell">Country</th>
                                             <th className="text-right font-medium uppercase tracking-wider text-[10px] px-4 py-2 w-10">Src</th>
                                          </tr>
                                       </thead>
                                       <tbody>
                                          {data.feed.map((item, idx) => (
                                             <tr key={idx} className="border-b border-sky-900/20 last:border-b-0 hover:bg-sky-900/15 transition-colors align-top">
                                                <td className="px-4 py-2.5 text-neutral-500 font-mono tabular-nums whitespace-nowrap">{formatDate(item.published_at)}</td>
                                                <td className="px-4 py-2.5">
                                                   <div className="text-neutral-100 font-medium truncate max-w-[200px]" title={item.victim_organization || ""}>
                                                      {item.victim_organization || "Unknown Target"}
                                                   </div>
                                                   {(item.victim_domain || item.victim_site) && (
                                                      <div className="text-[11px] text-sky-300/80 font-mono truncate max-w-[200px]">
                                                         {item.victim_domain || item.victim_site}
                                                      </div>
                                                   )}
                                                </td>
                                                <td className="px-4 py-2.5 text-neutral-300 hidden sm:table-cell">{item.victim_industry || "—"}</td>
                                                <td className="px-4 py-2.5 text-neutral-300 hidden sm:table-cell whitespace-nowrap">{item.victim_country || "—"}</td>
                                                <td className="px-4 py-2.5 text-right">
                                                   {item.published_url ? (
                                                      <button
                                                         onClick={() => setRedirectUrl(item.published_url)}
                                                         className="inline-flex p-1 rounded text-neutral-500 hover:text-sky-300 hover:bg-white/[0.06] transition-colors"
                                                         title="View source (external)"
                                                      >
                                                         <ExternalLink className="h-3.5 w-3.5" />
                                                      </button>
                                                   ) : (
                                                      <span className="text-neutral-700">—</span>
                                                   )}
                                                </td>
                                             </tr>
                                          ))}
                                       </tbody>
                                    </table>
                                 </div>
                              ) : (
                                 <div className="px-4 py-8 text-center text-[12px] text-neutral-500">No recently observed operations.</div>
                              )}
                           </div>
                        </>
                      )}
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>

    {/* --- SAFETY WARNING MODAL --- */}
    <Transition.Root show={!!redirectUrl} as={Fragment}>
        <Dialog as="div" className="relative z-[300]" onClose={() => setRedirectUrl(null)}>
            <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-black/75 transition-opacity" />
            </Transition.Child>
            
            <div className="fixed inset-0 z-10 overflow-y-auto">
                <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        enterTo="opacity-100 translate-y-0 sm:scale-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                        leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                    >
                        <Dialog.Panel className="relative transform overflow-hidden rounded-md bg-[#0f172a] border border-red-500/30 text-left shadow-2xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                            <div className="bg-[#0f172a] px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-red-900/20 sm:mx-0 sm:h-10 sm:w-10">
                                        <AlertOctagon className="h-6 w-6 text-red-500" aria-hidden="true" />
                                    </div>
                                    <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                        <Dialog.Title as="h3" className="text-[16px] font-semibold leading-6 text-white">
                                            External Redirection Warning
                                        </Dialog.Title>
                                        <div className="mt-2">
                                            <p className="text-sm text-neutral-300">
                                                You are about to leave the platform and visit an external URL. 
                                                This link often leads to a <strong>hacker forum</strong>, <strong>leak site</strong>, or <strong>unverified source</strong>.
                                            </p>
                                            <div className="mt-4 rounded-md bg-red-950/30 border border-red-500/20 p-3">
                                                <p className="text-xs text-red-300 font-mono break-all">
                                                    {redirectUrl}
                                                </p>
                                            </div>
                                            <p className="mt-4 text-xs text-neutral-400">
                                                <strong>Security Tip:</strong> We recommend opening this link in a sandboxed environment or a dedicated VM.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-[#06101e] px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6 border-t border-white/5">
                                <button
                                    type="button"
                                    className="inline-flex w-full justify-center rounded-md bg-red-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                                    onClick={() => {
                                        window.open(redirectUrl, '_blank');
                                        setRedirectUrl(null);
                                    }}
                                >
                                    Proceed (Unsafe)
                                </button>
                                <button
                                    type="button"
                                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white/5 px-3 py-2 text-sm font-semibold text-white shadow-sm ring-1 ring-inset ring-white/10 hover:bg-white/10 sm:mt-0 sm:w-auto"
                                    onClick={() => setRedirectUrl(null)}
                                >
                                    Cancel
                                </button>
                            </div>
                        </Dialog.Panel>
                    </Transition.Child>
                </div>
            </div>
        </Dialog>
    </Transition.Root>
    </>
  );
}